import { useReducer, Reducer } from "react";
import { createContainer } from "react-tracked";
import {
  AuthMode,
  BiometricType,
  DefaultSession,
} from "@ionic-enterprise/identity-vault";
import _filter from "lodash/fp/filter";
import _findIndex from "lodash/fp/findIndex";
import { isPlatform } from "@ionic/react";
import { MQ } from "../services/mq";

export interface stateITF {
  appId: any;
  deviceId: any;
  token: any;
  pushToken: any;
  wallet: any;
  aliases: any;
  user: any;
  userId: any;
  identity: any;
  email: any;
  notification: any;
  authorization: any;
  assetTransactions: any;
  newAccountFormData: any;
  authMode: AuthMode;
  hasSession: boolean;
  biometricType: BiometricType;
  showPinDialog: boolean;
  setApplicationPin: boolean;
  biometricsSupported: boolean;
  session: DefaultSession | undefined;
  pendingPush: any;
  pendingShares: any;
  pushTokenSyncLoading?: boolean;
  issuers?: any;
  isCreatingAliasToken?: any;
  isOnlyPin?: boolean;
  PINFailedAttempts?: number;
  clientReferenceId?: any;
  accountCreateApproved?: any;
  accountRecoverApproved?: any;
  accounts?: any;
  lastUser?: any;
  deletedAccounts?: any;
  hasOnboarding?: boolean;
  authorizationActionTimestamp: any;
  mqm: any;
}

const initialAccounFormData = {
  alias: "",
  appId: 0,
  expiry: "",
  burner: false,
  active: true,
  authMode: "5",
  timestamp: "",
};

let stateData: any = {
  token: localStorage["token"] ? JSON.parse(localStorage["token"]) : undefined, // for web
  userId: localStorage["userId"]
    ? JSON.parse(localStorage["userId"])
    : undefined, // for web
  identity: localStorage["identity"]
    ? JSON.parse(localStorage["identity"])
    : undefined, // for web
};

if (isPlatform("cordova")) {
  stateData = {
    token: undefined, // for device
    userId: undefined, // for device
    identity: undefined, // for device
  };
}

const initialState: stateITF = {
  appId: 0,
  deviceId: localStorage["deviceId"]
    ? JSON.parse(localStorage["deviceId"])
    : undefined,
  ...stateData,

  // token: undefined, // for device
  // userId: undefined, // for device
  // identity: undefined, // for device

  // persist these data as per https://d-protocol.slack.com/archives/C027F9364LQ/p1655985539887019
  token: localStorage["token"] ? JSON.parse(localStorage["token"]) : undefined, // for web
  userId: localStorage["userId"]
    ? JSON.parse(localStorage["userId"])
    : undefined, // for web
  identity: localStorage["identity"]
    ? JSON.parse(localStorage["identity"])
    : undefined, // for web

    pushToken: localStorage["pushToken"]
    ? JSON.parse(localStorage["pushToken"])
    : undefined,
  wallet: localStorage["wallet"] ? JSON.parse(localStorage["wallet"]) : {},
  aliases: localStorage["aliases"] ? JSON.parse(localStorage["aliases"]) : [],
  user: localStorage["user"] ? JSON.parse(localStorage["user"]) : {},

  assetTransactions: localStorage["assetTransactions"]
    ? JSON.parse(localStorage["assetTransactions"])
    : {},
  newAccountFormData: initialAccounFormData,
  notification: null,
  authorization: null,
  email: null,
  authMode: AuthMode.SecureStorage,
  biometricType: "none",
  hasSession: false,
  showPinDialog: false,
  setApplicationPin: false,
  biometricsSupported: false,
  session: undefined,
  pendingPush: localStorage["pendingPush"]
    ? JSON.parse(localStorage["pendingPush"])
    : undefined,
  pendingShares: localStorage["pendingShares"]
    ? JSON.parse(localStorage["pendingShares"])
    : [],
  pushTokenSyncLoading: false,
  issuers: localStorage["issuers"] ? JSON.parse(localStorage["issuers"]) : [],
  isCreatingAliasToken: null,
  isOnlyPin: false,
  PINFailedAttempts: localStorage["PINFailedAttempts"]
    ? Number(localStorage["PINFailedAttempts"])
    : 0,
  clientReferenceId: null,
  accountCreateApproved: false,
  accountRecoverApproved: false,
  accounts: localStorage["accounts"]
    ? JSON.parse(localStorage["accounts"])
    : [],
  lastUser: localStorage["lastUser"]
    ? JSON.parse(localStorage["lastUser"])
    : undefined,
  deletedAccounts: localStorage["deletedAccounts"]
    ? JSON.parse(localStorage["deletedAccounts"])
    : [],
  hasOnboarding: localStorage["hasOnboarding"]
    ? JSON.parse(localStorage["hasOnboarding"])
    : false,
  authorizationActionTimestamp: null,
  mqm: null
};

type State = typeof initialState;

type Action =
  | { type: "logout"; appId: any }
  | { type: "setAppId"; appId: any }
  | { type: "setDeviceId"; deviceId: any }
  | { type: "setToken"; token: any }
  | { type: "setPushToken"; pushToken: any }
  | { type: "setWallet"; wallet: any }
  | { type: "setAliases"; aliases: any }
  | { type: "setUser"; user: any }
  | { type: "setUserId"; userId: any }
  | { type: "setIdentity"; identity: any }
  | { type: "setNotification"; notification: any }
  | { type: "resetNewAccountFormData" }
  | { type: "setNewAccountFormData"; newAccountFormData: any }
  | { type: "setAuthorization"; authorization: any }
  | { type: "setAssetTransactions"; assetTransactions: any }
  | { type: "hideApplicationsPIN" }
  | { type: "showApplicationsPIN"; setApplicationsPin: boolean }
  | {
      type: "setVaultAuth";
      authMode: any;
      biometricType: any;
      hasSession: any;
      biometricsSupported: any;
    }
  | { type: "setSession"; session: DefaultSession | undefined }
  | { type: "setPendingPush"; pendingPush: any }
  | { type: "setPendingShare"; pendingShare: any }
  | { type: "setPendingShares"; pendingShares: any }
  | { type: "removePendingShare"; pendingShare: any }
  | { type: "setPushTokenSyncLoading"; pushTokenSyncLoading: any }
  | { type: "setIssuer"; issuer: any }
  | { type: "setIssuers"; issuers: any[] }
  | { type: "setIsCreatingAliasToken"; isCreatingAliasToken: any }
  | { type: "setIsOnlyPin" }
  | { type: "setPINFailedAttempts"; value?: number }
  | { type: "setClientReferenceId"; clientReferenceId: any }
  | { type: "setAccountCreateApproved"; accountCreateApproved: any }
  | { type: "setAccountRecoverApproved"; accountRecoverApproved: any }
  | { type: "setAccount"; account: any }
  | { type: "setLastUser"; lastUser: any }
  | { type: "setDeletedAccount"; deletedAccount: any }
  | { type: "recoverAccount"; account: any }
  | { type: "setHasOnboarding"; hasOnboarding: boolean }
  | { type: "setAuthorizationActionTimestamp"; authorizationActionTimestamp: any }
  | { type: "setMqm"; mqm: MQ | any };

const reducer: Reducer<State, Action> = (state, action) => {
  let newState = { ...state };

  switch (action.type) {
    case "setAppId": {
      newState.appId = action.appId;
      break;
    }
    case "setDeviceId": {
      newState.deviceId = action.deviceId;
      localStorage["deviceId"] = JSON.stringify(action.deviceId);
      break;
    }
    case "setToken": {
      newState.token = action.token;
      localStorage["token"] = JSON.stringify(action.token);
      break;
    }
    case "setPushToken": {
      newState.pushToken = action.pushToken;
      localStorage["pushToken"] = JSON.stringify(action.pushToken);
      break;
    }
    case "setWallet": {
      newState.wallet = action.wallet;
      localStorage["wallet"] = JSON.stringify(action.wallet);
      break;
    }
    case "setUser": {
      newState.user = action.user;
      localStorage["user"] = JSON.stringify(action.user);
      break;
    }
    case "setUserId": {
      newState.userId = action.userId;
      localStorage["userId"] = JSON.stringify(action.userId);
      break;
    }
    case "setIdentity": {
      newState.identity = action.identity;
      localStorage["identity"] = JSON.stringify(action.identity);
      break;
    }
    case "setAliases": {
      newState.aliases = [...action.aliases];
      localStorage["aliases"] = JSON.stringify(action.aliases);
      break;
    }
    case "setAssetTransactions": {
      newState.assetTransactions = {
        ...action.assetTransactions,
        timestamp: new Date().getTime(),
      };
      localStorage["assetTransactions"] = JSON.stringify({
        ...action.assetTransactions,
        timestamp: new Date().getTime(),
      });
      break;
    }
    case "setNotification": {
      newState.notification = {
        ...action.notification,
        timestamp: new Date().getTime(),
      };
      break;
    }
    case "setNewAccountFormData": {
      newState.newAccountFormData = { ...action.newAccountFormData };
      break;
    }
    case "resetNewAccountFormData": {
      newState.newAccountFormData = {
        alias: "",
        appId: 0,
        expiry: "",
        burner: false,
        active: true,
        authMode: "5",
      };
      break;
    }
    case "setVaultAuth": {
      newState.authMode = action.authMode;
      localStorage.setItem("authMode", newState.authMode.toString());
      newState.hasSession = action.hasSession;
      newState.biometricType = action.biometricType;
      newState.biometricsSupported = action.biometricsSupported;
      break;
    }
    case "setSession": {
      newState.session = action.session;
      localStorage["session"] = JSON.stringify(action.session);
      break;
    }

    case "hideApplicationsPIN": {
      newState.showPinDialog = false;
      newState.setApplicationPin = false;
      break;
    }
    case "showApplicationsPIN": {
      newState.showPinDialog = true;
      newState.setApplicationPin = action.setApplicationsPin;
      break;
    }
    case "setAuthorization": {
      newState.authorization = {
        ...action.authorization,
        timestamp: new Date().getTime(),
      };
      break;
    }
    case "setPendingPush": {
      if (action.pendingPush) {
        newState.pendingPush = { ...action.pendingPush };
        localStorage["pendingPush"] = JSON.stringify(action.pendingPush);
      } else {
        newState.pendingPush = action.pendingPush;
        localStorage["pendingPush"] = action.pendingPush;
      }
      break;
    }
    case "setPendingShare": {
      if (action.pendingShare) {
        newState.pendingShares = [
          ...newState.pendingShares,
          action.pendingShare,
        ];
        localStorage["pendingShares"] = JSON.stringify(newState.pendingShares);
      }
      break;
    }
    case "setPendingShares": {
      newState.pendingShares = [...action.pendingShares];
      localStorage["pendingShares"] = JSON.stringify(newState.pendingShares);
      break;
    }
    case "removePendingShare": {
      if (action.pendingShare) {
        const { keyid } = action.pendingShare;
        const tempPendingShares = _filter(
          (val: any) => val.keyid !== keyid,
          newState.pendingShares
        );

        newState.pendingShares = [...tempPendingShares];
        localStorage["pendingShares"] = JSON.stringify(newState.pendingShares);
      }
      break;
    }
    case "setPushTokenSyncLoading": {
      newState.pushTokenSyncLoading = action.pushTokenSyncLoading;
      break;
    }
    case "setIssuer": {
      if (action.issuer) {
        const { appId } = action.issuer;
        const index = _findIndex(
          (val: any) => val.appId === appId,
          newState.issuers
        );

        if (index !== -1) {
          newState.issuers.splice(index, 1, action.issuer);
          newState.issuers = [...newState.issuers];
        } else {
          newState.issuers = [...newState.issuers, action.issuer];
        }

        localStorage["issuers"] = JSON.stringify(newState.issuers);
      }
      break;
    }
    case "setIssuers": {
      if (action.issuers) {
        newState.issuers = [...action.issuers];
        localStorage["issuers"] = JSON.stringify(newState.issuers);
      }
      break;
    }
    case "setIsCreatingAliasToken": {
      newState.isCreatingAliasToken = action.isCreatingAliasToken;
      break;
    }
    case "setIsOnlyPin": {
      newState.isOnlyPin = false;
      break;
    }
    case "setPINFailedAttempts": {
      newState.PINFailedAttempts =
        action.value === undefined
          ? (state.PINFailedAttempts || 0) + 1
          : action.value;
      localStorage["PINFailedAttempts"] = newState.PINFailedAttempts;
      break;
    }
    case "setClientReferenceId": {
      newState.clientReferenceId = action.clientReferenceId;
      break;
    }
    case "setAccountCreateApproved": {
      newState.accountCreateApproved = action.accountCreateApproved;
      break;
    }
    case "setAccountRecoverApproved": {
      newState.accountRecoverApproved = action.accountRecoverApproved;
      break;
    }
    case "setAccount": {
      if (action.account) {
        const { identity } = action.account;
        const index = _findIndex(
          (val: any) => val.identity === identity,
          newState.accounts
        );

        if (index !== -1) {
          newState.accounts.splice(index, 1, action.account);
          newState.accounts = [...newState.accounts];
        } else {
          newState.accounts = [...newState.accounts, action.account];
        }

        localStorage["accounts"] = JSON.stringify(newState.accounts);
      }
      break;
    }
    case "setLastUser": {
      if (action.lastUser) {
        newState.lastUser = action.lastUser;
        localStorage["lastUser"] = JSON.stringify(newState.lastUser);
      }
      break;
    }
    case "setDeletedAccount": {
      if (action.deletedAccount) {
        newState.deletedAccounts = [
          ...newState.deletedAccounts,
          action.deletedAccount,
        ];
        localStorage["deletedAccounts"] = JSON.stringify(
          newState.deletedAccounts
        );

        const { identity } = action.deletedAccount;
        const updatedAccounts = _filter(
          (val: any) => val.identity !== identity,
          newState.accounts
        );

        newState.accounts = [...updatedAccounts];
        localStorage["accounts"] = JSON.stringify(newState.accounts);
      }
      break;
    }
    case "recoverAccount": {
      if (action.account) {
        const { identity } = action.account;

        const updatedAccounts = _filter(
          (val: any) => val.identity !== identity,
          newState.deletedAccounts
        );

        newState.deletedAccounts = [...updatedAccounts];
        localStorage["deletedAccounts"] = JSON.stringify(
          newState.deletedAccounts
        );

        const existing = newState.accounts.find(
          (val: any) => val.identity === identity
        );
        if (!existing) {
          newState.accounts = [...newState.accounts, action.account];
        }

        localStorage["accounts"] = JSON.stringify(newState.accounts);
      }
      break;
    }
    case "setHasOnboarding": {
      newState.hasOnboarding = action.hasOnboarding;
      localStorage["hasOnboarding"] = JSON.stringify(newState.hasOnboarding);
      break;
    }
    case "setAuthorizationActionTimestamp": {
      newState.authorizationActionTimestamp = action.authorizationActionTimestamp;
      break;
    }
    case "setMqm": {
      newState.mqm = action.mqm;
      break;
    }
    case "logout": {
      if (action.appId === process.env.REACT_APP_APP_ID) {
        localStorage.removeItem("token");
        localStorage.removeItem("userId");
        localStorage.removeItem("identity");
        // localStorage.removeItem('pushToken');
        localStorage.removeItem("wallet");
        localStorage.removeItem("aliases");
        localStorage.removeItem("session");
        localStorage.removeItem("assetTransactions");
        localStorage.removeItem("pendingShares");
        localStorage["hasFirstTimeLogin"] = JSON.stringify(true);
        localStorage.removeItem("issuers");
        localStorage.removeItem("isOnlyPin");
        localStorage.removeItem("PINFailedAttempts");
        newState = {
          ...initialState,
          token: null,
          pushToken: null,
          wallet: null,
          aliases: null,
          user: newState.user,
          userId: null,
          identity: null,
          assetTransactions: null,
          authMode: newState.authMode,
          hasSession: newState.hasSession,
          biometricType: newState.biometricType,
          biometricsSupported: newState.biometricsSupported,
          PINFailedAttempts: 0,
          pendingShares: [],
          issuers: [],
          clientReferenceId: newState.clientReferenceId,
          accountCreateApproved: false,
          accountRecoverApproved: false,
          accounts: newState.accounts,
          lastUser: newState.lastUser,
          deletedAccounts: newState.deletedAccounts,
          hasOnboarding: newState.hasOnboarding,
          authorizationActionTimestamp: null,
          mqm: newState.mqm,
        };
      }
      break;
    }
    default: {
      throw new Error("unknown action type");
    }
  }

  return newState;
};

const useValue = () => useReducer(reducer, initialState);

export const { Provider, useTracked } = createContainer(useValue);
